// apps/web/src/self-service/state.ts — Phase 4 plan 04-04.
//
// In-memory store for the self-service surface (A8 / A9 / A10). The
// plan calls for `zustand`; the threat-model section (T-04-SC, no
// new packages) forbids new npm packages. The state shape is small
// enough to live in `useSyncExternalStore` + module-scope object —
// the same pattern the 04-03 wizard store uses (see
// `apps/web/src/wizard/state.ts`).
//
// SECURITY INVARIANT (T-04-32 / Pitfall 6):
//   The raw self-service token is NEVER persisted to localStorage or
//   sessionStorage. A shared device, a malicious browser extension,
//   or an XSS-vulnerable adjacent origin would leak the credential.
//   The store holds the token in module-scope memory only; closing
//   the tab discards it. The 24h server-side expiry is the
//   authoritative cap on the credential's lifetime (D-02).
//
// The store also exposes `getSelfServiceTokenStore()` — an
// imperative accessor used by the GraphQL request header builder in
// `apps/web/src/lib/api.ts` (mirrors the way `getAccessToken()` is
// used for the admin auth header). The Apollo `setContext` link in
// the plan collapses to a synchronous getter on the same module-
// scope object because graphql-request re-reads the header map on
// every request.

import { useSyncExternalStore } from "react";

// ===== Types =====

export interface SelfServiceReservation {
  id: string;
  reservationNumber: string;
  status: "bestätigt" | "angefragt" | "storniert" | "no_show";
  partySize: number;
  serviceDate: string;
  slotDefinitionId: string;
  startsAt: string;
  durationMinutes: number;
  areaId: string | null;
  confirmationMode: "AUTO" | "MANUAL";
  isCancellable: boolean;
  // The OWNER-ONLY fields (returned by modify + cancel). SELF-01
  // does NOT populate them; the public-safe read returns "" / null
  // for `guestName` / `guestEmail` / `guestPhone` per D-09 / T-04-14.
  guestName: string;
  guestEmail: string | null;
  guestPhone: string | null;
  createdAt: string;
  consentAt: string;
}

export interface SelfServiceState {
  token: string | null;
  reservation: SelfServiceReservation | null;
}

const INITIAL_STATE: SelfServiceState = {
  token: null,
  reservation: null,
};

// ===== Store =====

let state: SelfServiceState = INITIAL_STATE;

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

/** Imperative accessor for non-React consumers (the api.ts header builder). */
export function getSelfServiceTokenStore(): { token: string | null } {
  return { token: state.token };
}

export function getSelfServiceState(): SelfServiceState {
  return state;
}

export function setSelfServiceToken(token: string | null): void {
  state = { ...state, token };
  emit();
}

export function setSelfServiceReservation(
  reservation: SelfServiceReservation | null,
): void {
  state = { ...state, reservation };
  emit();
}

/**
 * Reset the store to its initial state. Called when the guest
 * cancels the reservation (A11) and clicks "Neue Reservierung".
 * The token is discarded so the next wizard run is forced to
 * navigate via a fresh createReservation.
 */
export function clearSelfService(): void {
  state = INITIAL_STATE;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Subscribe to the entire state object. */
export function useSelfService(): SelfServiceState {
  return useSyncExternalStore(
    subscribe,
    getSelfServiceState,
    getSelfServiceState,
  );
}

/**
 * Subscribe to a single slice; re-renders only when the comparison
 * function returns false. Mirrors the `useWizardSlice` helper in
 * `wizard/state.ts`.
 */
export function useSelfServiceSlice<T>(
  pick: (s: SelfServiceState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T {
  return useSyncExternalStore(
    subscribe,
    () => {
      const next = pick(getSelfServiceState());
      const cached = sliceCache.get(pick) as { prev: T; value: T } | undefined;
      if (cached && isEqual(cached.prev, next)) {
        return cached.value;
      }
      const value = next;
      sliceCache.set(pick, { prev: next, value });
      return value;
    },
    () => getSelfServiceState() as unknown as T,
  );
}

const sliceCache = new WeakMap<
  (s: SelfServiceState) => unknown,
  { prev: unknown; value: unknown }
>();
